/**
 * views/PosPinAdminView.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Platform-admin page for resetting POS terminal PINs on behalf of any vendor.
 *
 * Layout:
 *   - Top: debounced vendor typeahead (GET /api/admin/entity-search?type=VENDOR).
 *     The selected result's `id` is the vendor's user _id, which is exactly the
 *     `vendorUserId` that the cashier list + admin PIN endpoints key on.
 *   - Below: <PosPinManager admin vendorUserId=… /> renders the vendor's cashier
 *     roster and per-cashier PIN reset controls (PUT /api/pos/admin/cashier-pin).
 *
 * Only SUPERUSER / ADMIN / SUPERVISOR users reach this route (no entitlementKey,
 * admin-only sidebar group). The backend admin PIN endpoint independently gates
 * on the caller's role, so this view is a convenience layer, not the guard.
 *
 * @see controllers/posController.js (adminSetCashierPin, listCashiersWithPinStatus)
 * @see components/Pos/PosPinManager.js
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { apiEntitySearch } from "../api/index.js";
import PosPinManager from "../components/Pos/PosPinManager.js";

const S = {
  searchWrap: { position: "relative", maxWidth: 520 },
  input: {
    width: "100%", padding: "11px 14px", border: "1.5px solid #e5e7eb",
    borderRadius: 10, fontSize: "0.95rem",
  },
  dropdown: {
    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden", maxHeight: 320, overflowY: "auto",
  },
  option: { padding: "12px 14px", cursor: "pointer", borderBottom: "1px solid #f3f4f6" },
  optName: { fontWeight: 600, color: "#111827", fontSize: "0.9rem" },
  optMeta: { fontSize: "0.78rem", color: "#6b7280", marginTop: 2 },
  selected: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 10,
    padding: "12px 16px", maxWidth: 520, marginBottom: 18,
  },
  clearBtn: {
    border: "none", background: "transparent", color: "#0e7490",
    fontWeight: 700, cursor: "pointer", fontSize: "0.82rem",
  },
};

const PosPinAdminView = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [vendor, setVendor] = useState(null); // { id, label, subLabel }
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await apiEntitySearch("VENDOR", q.trim(), 10);
      setResults(data.results || []);
      setOpen(true);
    } catch (_err) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const pick = (v) => {
    setVendor(v);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  return (
    <Container fluid style={{ paddingTop: 16 }}>
      <Row>
        <Col md={12}>
          <Card style={{ borderRadius: 14, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <Card.Body>
              <h4 style={{ margin: 0, fontWeight: 700, color: "#111827" }}>POS Terminal PINs (Admin)</h4>
              <p style={{ color: "#6b7280", fontSize: "0.85rem", margin: "6px 0 18px" }}>
                Look up any vendor and reset the 4-digit PIN their cashiers use to sign in to the
                Lusuku POS terminal.
              </p>

              {vendor ? (
                <div style={S.selected}>
                  <div>
                    <div style={S.optName}>{vendor.label}</div>
                    <div style={S.optMeta}>{vendor.subLabel}</div>
                  </div>
                  <button style={S.clearBtn} onClick={() => setVendor(null)}>
                    Change vendor
                  </button>
                </div>
              ) : (
                <div style={S.searchWrap}>
                  <input
                    style={S.input}
                    placeholder="Search vendor by name or phone…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => results.length && setOpen(true)}
                  />
                  {searching && (
                    <div style={{ position: "absolute", right: 12, top: 11 }}>
                      <Spinner animation="border" size="sm" variant="secondary" />
                    </div>
                  )}
                  {open && results.length > 0 && (
                    <div style={S.dropdown}>
                      {results.map((v) => (
                        <div
                          key={v.id}
                          style={S.option}
                          onMouseDown={() => pick(v)}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fdff")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                        >
                          <div style={S.optName}>
                            {v.label}{" "}
                            {v.verified && (
                              <span style={{ color: "#16a34a", fontSize: "0.72rem", fontWeight: 700 }}>
                                ✓ verified
                              </span>
                            )}
                          </div>
                          <div style={S.optMeta}>{v.subLabel}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {open && !searching && query.trim().length >= 2 && results.length === 0 && (
                    <div style={S.dropdown}>
                      <div style={{ padding: "14px", color: "#6b7280", fontSize: "0.85rem" }}>
                        No vendors match “{query.trim()}”.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {vendor && (
        <Row style={{ marginTop: 18 }}>
          <Col md={12}>
            <PosPinManager
              admin
              vendorUserId={String(vendor.id)}
              title={`Terminal PINs — ${vendor.label}`}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default PosPinAdminView;
